import { PLAYER_H, PLAYER_W, RUN_MAX, SUB } from '../shared/constants.ts';
import type { Sim } from '../shared/sim.ts';

interface Puff {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

const SHOES = '#f2f0ff';
const SCARF = '#ff4d6d';
const THIGH = 2.3;
const SHIN = 2.3;

/**
 * The player character: a little runner drawn inside the unchanged 10×14 px
 * hitbox. Purely cosmetic: all animation state lives here, never in the sim,
 * so replays stay deterministic. Legs are driven by distance travelled (no
 * moonwalking), with poses for running, jumping, falling, skidding and idling.
 */
export class Runner {
  private key = '';
  private lastX = 0;
  private lastVx = 0;
  private lastVy = 0;
  private wasGround = true;
  private phase = 0;
  private facing = 1;
  private land = 0;
  private skid = 0;
  private blinkAt = 0;
  private puffs: Puff[] = [];

  draw(ctx: CanvasRenderingContext2D, sim: Sim, key: string, cam: number, color: string, now: number, dt: number): void {
    if (key !== this.key) {
      this.key = key;
      this.lastX = sim.x;
      this.lastVx = sim.vx;
      this.lastVy = sim.vy;
      this.wasGround = sim.onGround;
      this.phase = 0;
      this.facing = 1;
      this.land = 0;
      this.skid = 0;
    }
    const pw = PLAYER_W / SUB;
    const ph = PLAYER_H / SUB;
    const fx = sim.x / SUB - cam + pw / 2; // feet, centre
    const fy = sim.y / SUB + ph;
    const speed = Math.min(1, Math.abs(sim.vx) / RUN_MAX);

    // ---- update (per rendered frame; the sim may have advanced several ticks)
    const dx = Math.min(12, Math.abs(sim.x - this.lastX) / SUB);
    if (sim.vx > 3) this.facing = 1;
    else if (sim.vx < -3) this.facing = -1;
    if (sim.onGround) this.phase += dx * 0.26;
    const braking = sim.onGround && Math.abs(sim.vx) > 8 && Math.abs(sim.vx) < Math.abs(this.lastVx);
    this.skid += ((braking ? 1 : 0) - this.skid) * Math.min(1, dt * 18);
    if (sim.onGround && !this.wasGround) {
      const impact = Math.min(1, this.lastVy / 110);
      this.land = 0.08 + 0.14 * impact;
      if (impact > 0.4) this.dust(fx + cam, fy, 6, 0);
    }
    if (braking && Math.random() < dt * 25) this.dust(fx + cam, fy, 1, this.facing);
    this.land = Math.max(0, this.land - dt);
    this.lastX = sim.x;
    this.lastVx = sim.vx;
    this.lastVy = sim.vy;
    this.wasGround = sim.onGround;
    if (now > this.blinkAt + 140) this.blinkAt = now + 2000 + Math.random() * 3000;
    const blinking = now > this.blinkAt;

    this.drawPuffs(ctx, cam, dt);

    // ---- pose: angles in radians from straight down, positive = forward
    let legs: [number, number][]; // [thigh, shin] for front and back leg
    let arms: [number, number];
    let bob = 0;
    if (!sim.onGround) {
      if (sim.vy < 0) {
        legs = [[1.0, -0.2], [-0.5, -1.1]];
        arms = [2.5, 2.0];
      } else {
        const f = Math.sin(now / 55) * 0.25;
        legs = [[0.45 + f, 0.2], [-0.35 - f, -0.1]];
        arms = [2.2 + f, 1.6 - f];
      }
    } else if (this.skid > 0.5) {
      legs = [[0.7, 0.75], [-0.25, -0.6]];
      arms = [1.3, 0.9];
    } else if (speed > 0.08) {
      const amp = Math.min(1, speed * 1.4);
      const leg = (p: number): [number, number] => {
        const t = Math.sin(p) * 0.8 * amp;
        const bend = Math.max(0, -Math.cos(p)) * 1.3 * amp;
        return [t, t - bend];
      };
      legs = [leg(this.phase), leg(this.phase + Math.PI)];
      arms = [-Math.sin(this.phase) * 0.9 * amp, Math.sin(this.phase) * 0.9 * amp];
      bob = -Math.abs(Math.sin(this.phase)) * 0.7 * amp;
    } else {
      legs = [[0.12, 0.05], [-0.1, -0.05]];
      arms = [0.15, -0.1];
      bob = Math.sin(now / 450) * 0.3;
    }

    // Squash on landing, stretch in the air, lean into the run (or back, when skidding).
    let sx = 1;
    let sy = 1;
    if (this.land > 0) {
      const l = Math.min(1, this.land / 0.12);
      sx = 1 + 0.22 * l;
      sy = 1 - 0.2 * l;
    } else if (!sim.onGround) {
      const s = Math.max(-0.12, Math.min(0.12, -sim.vy / 900));
      sx = 1 - s;
      sy = 1 + s;
    }
    const lean = sim.onGround ? speed * 0.14 - this.skid * 0.3 : 0.05;

    ctx.save();
    ctx.translate(fx, fy);
    ctx.scale(this.facing * sx, sy);
    ctx.rotate(lean);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Limbs in darker shades of the body colour: readable on the dark sky and against the body.
    const limb = shade(color, -0.45);
    const limbBack = shade(color, -0.62);
    const hip = { x: 0, y: -4.5 + bob };
    const shoulder = { x: 0.4, y: -6.6 + bob };

    this.arm(ctx, shoulder, arms[1], limbBack);
    this.leg(ctx, hip, legs[1], limbBack);
    // Torso (hoodie).
    ctx.fillStyle = shade(color, -0.2);
    roundRect(ctx, -2.5, -7.6 + bob, 5.2, 3.6, 1.4);
    this.leg(ctx, hip, legs[0], limb);
    // Head.
    const hy = -14 + bob;
    ctx.fillStyle = color;
    roundRect(ctx, -4.1, hy, 8.8, 6.3, 3);
    // Eyes, looking ahead.
    ctx.fillStyle = '#fff';
    const eh = blinking ? 0.5 : 2.6;
    const ey = hy + 1.8 + (2.6 - eh) / 2;
    ctx.fillRect(0.6, ey, 1.7, eh);
    ctx.fillRect(2.9, ey, 1.7, eh);
    if (!blinking) {
      ctx.fillStyle = '#111';
      const look = sim.vy < -20 ? -0.5 : sim.vy > 40 ? 0.6 : 0;
      ctx.fillRect(1.4, ey + 0.8 + look, 0.9, 1.3);
      ctx.fillRect(3.7, ey + 0.8 + look, 0.9, 1.3);
    }
    // Scarf: trails behind with speed, flutters.
    const len = 1.5 + speed * 4 + (sim.onGround ? 0 : 1.5);
    ctx.strokeStyle = SCARF;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(-1.8, -7.2 + bob);
    for (let i = 1; i <= 3; i++) {
      const t = i / 3;
      ctx.lineTo(-1.8 - len * t, -7.2 + bob + Math.sin(now / 70 + i * 1.7) * (0.3 + speed) * t + (1 - speed) * t * 1.5);
    }
    ctx.stroke();
    ctx.fillStyle = SCARF;
    ctx.fillRect(-2.5, -7.8 + bob, 4.8, 1.2);
    this.arm(ctx, shoulder, arms[0], limb);
    ctx.restore();
  }

  private leg(ctx: CanvasRenderingContext2D, hip: { x: number; y: number }, [thigh, shin]: [number, number], color: string): void {
    const kx = hip.x + Math.sin(thigh) * THIGH;
    const ky = hip.y + Math.cos(thigh) * THIGH;
    const fx = kx + Math.sin(shin) * SHIN;
    const fy = ky + Math.cos(shin) * SHIN;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(hip.x, hip.y);
    ctx.lineTo(kx, ky);
    ctx.lineTo(fx, fy);
    ctx.stroke();
    ctx.fillStyle = SHOES;
    ctx.fillRect(fx - 0.9, fy - 0.9, 2.6, 1.5);
  }

  private arm(ctx: CanvasRenderingContext2D, s: { x: number; y: number }, a: number, color: string): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    const hx = s.x + Math.sin(a) * 2.9;
    const hy = s.y + Math.cos(a) * 2.9;
    ctx.lineTo(hx, hy);
    ctx.stroke();
    ctx.fillStyle = SHOES;
    ctx.beginPath();
    ctx.arc(hx, hy, 0.8, 0, Math.PI * 2);
    ctx.fill();
  }

  private dust(x: number, y: number, n: number, dir: number): void {
    for (let i = 0; i < n; i++) {
      const side = dir || (i % 2 ? 1 : -1);
      this.puffs.push({ x: x + side * 2, y: y - 1, vx: side * (8 + Math.random() * 18), vy: -6 - Math.random() * 10, life: 1 });
    }
  }

  private drawPuffs(ctx: CanvasRenderingContext2D, cam: number, dt: number): void {
    this.puffs = this.puffs.filter((p) => (p.life -= dt * 2.5) > 0);
    for (const p of this.puffs) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      ctx.globalAlpha = p.life * 0.5;
      ctx.fillStyle = '#d8d3ee';
      ctx.beginPath();
      ctx.arc(p.x - cam, p.y, 1 + (1 - p.life) * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

/** Lighten (amt > 0) or darken (amt < 0) a #rrggbb colour. */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.round(amt < 0 ? c * (1 + amt) : c + (255 - c) * amt);
  return `rgb(${f(n >> 16)}, ${f((n >> 8) & 255)}, ${f(n & 255)})`;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.fill();
}
